import { Search } from "lucide-react";
import { useMemo } from "react";

import { PageLayout, PageTitle } from "components/layout";
import { Button } from "components/ui/button";
import { toast } from "@/components/ui/use-toast";
import {
  IStoreDeposit,
  IStoreDepositAccount,
  IStoreDepositParams,
  IUpdateDepositNsuResult,
} from "src/@types/store-deposit-maintenance";
import {
  useGetStoreDepositAccountsQuery,
  useGetStoreDepositAllowedStoresQuery,
  useGetStoreDepositsQuery,
  useUpdateStoreDepositAccountMutation,
  useUpdateStoreDepositNsuMutation,
} from "@/services/store-deposit-maintenance";
import { useGetStoresOptionsQuery } from "@/services/store/select-options";
import { layoutClasses } from "@/utils/css-classes";

import { BankDetailsModal } from "./components/bank-details-modal";
import { DepositFilters } from "./components/deposit-filters";
import { DepositPagination } from "./components/deposit-pagination";
import { DepositTable } from "./components/deposit-table";
import { UpdateAccountModal } from "./components/update-account-modal";
import { UpdateNsuModal } from "./components/update-nsu-modal";
import { depositIdentity, toDepositKey } from "./deposit-formatters";
import { useDepositFilterReducer } from "./reducers/useDepositFilterReducer";
import { useDepositModalReducer } from "./reducers/useDepositModalReducer";
import {
  DEPOSIT_MAINTENANCE_LABELS,
  DEPOSIT_MAINTENANCE_MESSAGES,
  FilterOption,
  GLOBALLY_HANDLED_STATUS,
  NSU_UPDATE_STATUS,
} from "./types";

interface ToastMessage {
  readonly description: string;
  readonly variant?: "destructive";
}

const toNumber = (value: string | undefined): number | undefined => {
  if (value === undefined) return undefined;
  const parsed: number = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const trimmedOrUndefined = (value: string): string | undefined =>
  value.trim() === "" ? undefined : value.trim();

export default function StoreDepositMaintenancePage() {
  const { state: filters, methods: filterMethods } = useDepositFilterReducer();
  const { draft, applied, page, perPage } = filters;
  const { state: modal, methods: modalMethods } = useDepositModalReducer();

  const { data: storeOptions } = useGetStoresOptionsQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });
  const { data: allowedStores } = useGetStoreDepositAllowedStoresQuery();

  const allowedCodes: number[] | null = allowedStores?.storeCodes ?? null;

  const availableStores = useMemo(() => {
    const all = (storeOptions ?? []) as FilterOption[];
    if (allowedCodes === null) return all;
    return all.filter((option: FilterOption) =>
      allowedCodes.includes(Number(option.value)),
    );
  }, [storeOptions, allowedCodes]);

  const isStoreLocked: boolean =
    allowedCodes !== null && availableStores.length === 1;

  const storeCode: number | undefined = isStoreLocked
    ? Number(availableStores[0].value)
    : draft.storeCode;

  const {
    data: deposits,
    isFetching,
    refetch,
  } = useGetStoreDepositsQuery(
    applied === undefined
      ? (undefined as unknown as IStoreDepositParams)
      : { ...applied, page, perPage },
    { skip: applied === undefined },
  );

  const { data: accounts, isFetching: isFetchingAccounts } =
    useGetStoreDepositAccountsQuery(storeCode as number, {
      skip: storeCode === undefined,
    });

  const [updateNsu, { isLoading: isSavingNsu }] =
    useUpdateStoreDepositNsuMutation();
  const [updateAccount, { isLoading: isSavingAccount }] =
    useUpdateStoreDepositAccountMutation();

  const activeAccounts: IStoreDepositAccount[] = useMemo(
    () => accounts ?? [],
    [accounts],
  );

  const findNsuLength = (deposit: IStoreDeposit | null): number | undefined => {
    if (deposit === null || deposit.bankNumber === null) return undefined;
    return activeAccounts.find(
      (account: IStoreDepositAccount) =>
        account.bankNumber === deposit.bankNumber,
    )?.nsuLength;
  };

  const findBankName = (deposit: IStoreDeposit | null): string | undefined =>
    deposit?.bankName ?? undefined;

  const hasRequiredFilters: boolean =
    storeCode !== undefined &&
    draft.startDate !== undefined &&
    draft.endDate !== undefined;

  const handleSearch = (): void => {
    const { startDate, endDate } = draft;

    if (
      storeCode === undefined ||
      startDate === undefined ||
      endDate === undefined
    ) {
      toast(DEPOSIT_MAINTENANCE_MESSAGES.MISSING_FILTERS);
      return;
    }

    filterMethods.applyFilters({
      storeCode,
      startDate,
      endDate,
      minAmount: draft.minAmount,
      maxAmount: draft.maxAmount,
      nsu: trimmedOrUndefined(draft.nsu),
      linkFilter: toNumber(draft.linkFilter),
    });
  };

  const notifyFailure = (error: unknown, message: ToastMessage): void => {
    const status = (error as { status?: number | string })?.status;
    if (GLOBALLY_HANDLED_STATUS.has(status as number)) return;
    toast(message);
  };

  const refetchDeposits = async (): Promise<void> => {
    if (applied === undefined) return;
    await refetch();
  };

  const handleSubmitNsu = async (value: string): Promise<void> => {
    if (modal.kind !== "nsu") return;

    try {
      const result: IUpdateDepositNsuResult = await updateNsu({
        ...toDepositKey(modal.deposit),
        nsu: value,
      }).unwrap();

      if (result.status === NSU_UPDATE_STATUS.CONFIRMATION_REQUIRED) {
        modalMethods.requireNsuConfirmation(result);
        return;
      }

      modalMethods.close();
      toast(DEPOSIT_MAINTENANCE_MESSAGES.NSU_UPDATED);
    } catch (error) {
      notifyFailure(error, DEPOSIT_MAINTENANCE_MESSAGES.NSU_ERROR);
      return;
    }

    await refetchDeposits();
  };

  const handleSubmitAccount = async (
    accountSequence: number,
  ): Promise<void> => {
    if (modal.kind !== "account") return;

    try {
      await updateAccount({
        ...toDepositKey(modal.deposit),
        accountSequence,
      }).unwrap();

      modalMethods.close();
      toast(DEPOSIT_MAINTENANCE_MESSAGES.ACCOUNT_UPDATED);
    } catch (error) {
      notifyFailure(error, DEPOSIT_MAINTENANCE_MESSAGES.ACCOUNT_ERROR);
      return;
    }

    await refetchDeposits();
  };

  return (
    <PageLayout>
      <div className={layoutClasses.pageContainer}>
        <PageTitle
          title={DEPOSIT_MAINTENANCE_LABELS.pageTitle}
          description={DEPOSIT_MAINTENANCE_LABELS.pageDescription}
          titleSize="text-3xl"
          descriptionSize="text-xl"
        />

        <DepositFilters
          storeOptions={availableStores}
          storeCode={storeCode}
          isStoreLocked={isStoreLocked}
          onStoreChange={filterMethods.setStore}
          startDate={draft.startDate}
          endDate={draft.endDate}
          onPeriodChange={filterMethods.setPeriod}
          minAmount={draft.minAmount}
          maxAmount={draft.maxAmount}
          onMinAmountChange={filterMethods.setMinAmount}
          onMaxAmountChange={filterMethods.setMaxAmount}
          nsu={draft.nsu}
          onNsuChange={filterMethods.setNsu}
          linkFilter={draft.linkFilter}
          onLinkFilterChange={filterMethods.setLinkFilter}
        />

        <div className="flex gap-3 mt-4">
          <Button
            onClick={handleSearch}
            disabled={!hasRequiredFilters || isFetching}
            className="h-8 bg-slate-800 hover:bg-slate-900 text-white"
          >
            <Search className="w-4 h-4 mr-2" />
            {isFetching
              ? DEPOSIT_MAINTENANCE_LABELS.searching
              : DEPOSIT_MAINTENANCE_LABELS.searchButton}
          </Button>
        </div>

        <DepositTable
          deposits={deposits?.data ?? []}
          isLoading={isFetching}
          emptyMessage={
            applied === undefined
              ? DEPOSIT_MAINTENANCE_LABELS.selectFiltersHint
              : DEPOSIT_MAINTENANCE_LABELS.emptyResult
          }
          onEditNsu={modalMethods.openNsu}
          onViewBankDetails={modalMethods.openDetails}
          onEditAccount={modalMethods.openAccount}
        />

        <DepositPagination
          page={page}
          pageSize={perPage}
          total={deposits?.total ?? 0}
          isFetching={isFetching}
          onPageChange={filterMethods.setPage}
          onPageSizeChange={filterMethods.setPageSize}
        />

        {modal.kind === "nsu" && (
          <UpdateNsuModal
            key={depositIdentity(modal.deposit)}
            deposit={modal.deposit}
            nsuLength={findNsuLength(modal.deposit)}
            isSaving={isSavingNsu}
            confirmation={modal.confirmation}
            onClose={modalMethods.close}
            onSubmit={handleSubmitNsu}
          />
        )}

        {modal.kind === "account" && (
          <UpdateAccountModal
            key={depositIdentity(modal.deposit)}
            deposit={modal.deposit}
            accounts={activeAccounts}
            isLoadingAccounts={isFetchingAccounts}
            isSaving={isSavingAccount}
            onClose={modalMethods.close}
            onSubmit={handleSubmitAccount}
          />
        )}

        {modal.kind === "details" && (
          <BankDetailsModal
            deposit={modal.deposit}
            bankName={findBankName(modal.deposit)}
            onClose={modalMethods.close}
          />
        )}
      </div>
    </PageLayout>
  );
}
